import React, { useEffect, useMemo, useState } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Grid,
  Box,
  Button,
  TextField,
  InputAdornment,
  Card,
  Typography,
} from "@mui/material";
import {
  useDeleteUserRoleMutation,
  useGetAllUserPrivilegesQuery,
  useGetUserInfoOnyQuery,
  useGetUserRoleQuery,
} from "../../../Api/attoDeskApi";
import {
  IUserAdmin,
  IUserRole,
  IPrivilege,
} from "../../../Api/Interface/api.interface";
import NewUpdatePrivi from "../Edit/index";
import DeletePopup from "../../../Components/Delete/DeletePopup";
import { useNotifier } from "../../../Core/Notifier";
import useDebounce from "../../../Components/SearchDebonce/useDebounce";
import { CiSearch } from "react-icons/ci";
import { Link, useNavigate } from "react-router-dom";
import { useStyles } from "../../../theme/appStyles";
import DeleteOutlineSharpIcon from "@mui/icons-material/DeleteOutlineSharp";
import EditSharpIcon from "@mui/icons-material/EditSharp";
import Pagination from "../../../Components/Pagination";
import { toProperCase } from "../../../Components/Validation/toProperCase";
import dayjs, { Dayjs } from "dayjs";
import useIsMobile from "../../../Core/hook/useIsMobile";
import { Privileges } from "../../../Core/Enum/enum";

type MenuItem = {
  title: string;
  submenuOpen: boolean;
  privilege?: string;
  privileges?: string[];
  submenus?: {
    title: string;
    privilege: string;
  }[];
};

const MainMenuList: {
  comomTitle: string;
  menuList?: MenuItem[];
}[] = [
  {
    comomTitle: "General",
    menuList: [
      {
        title: "Dashboard",
        submenuOpen: false,
        privilege: Privileges.DashboardView,
      },
      {
        title: "Purchase Dashboard",
        submenuOpen: false,
        privilege: Privileges.DashboardView,
      },
    ],
  },
  {
    comomTitle: "Purchase",
    menuList: [
      {
        title: "Purchase",
        submenuOpen: true,
        privileges: [
          Privileges.OrderList,
          Privileges.MainItemsView,
          Privileges.ItemsView,
          Privileges.CategoriesView,
          Privileges.LoopingView,
        ],
        submenus: [
          { title: "Order", privilege: Privileges.OrderList },
          { title: "Products", privilege: Privileges.MainItemsView },
          { title: "Variants", privilege: Privileges.ItemsView },
          { title: "Category", privilege: Privileges.CategoriesView },
          { title: "Looping", privilege: Privileges.LoopingView },
        ],
      },
      {
        title: "Fields",
        submenuOpen: true,
        privileges: [
          Privileges.StoreView,
          Privileges.TagView,
          Privileges.BrandView,
          Privileges.TaxView,
          Privileges.PrinterView,
        ],
        submenus: [
          { title: "Supplier", privilege: Privileges.StoreView },
          { title: "Tag", privilege: Privileges.TagView },
          { title: "Brand", privilege: Privileges.BrandView },
          { title: "Tax", privilege: Privileges.TaxView },
          { title: "Printer", privilege: Privileges.PrinterView },
        ],
      },
    ],
  },
  {
    comomTitle: "Staff",
    menuList: [
      {
        title: "Staff",
        submenuOpen: false,
        privilege: Privileges.StaffView,
      },
      {
        title: "Additional",
        submenuOpen: true,
        privileges: [Privileges.DepartmentView, Privileges.PositionView],
        submenus: [
          { title: "Department", privilege: Privileges.DepartmentView },
          { title: "Position", privilege: Privileges.PositionView },
        ],
      },
    ],
  },
  {
    comomTitle: "Schedule & Punch",
    menuList: [
      {
        title: "Clock In/Out",
        submenuOpen: false,
        privilege: Privileges.ScheduleView,
      },
      {
        title: "Schedule",
        submenuOpen: false,
        privilege: Privileges.ScheduleView,
      },
      {
        title: "Payroll",
        submenuOpen: false,
        privilege: Privileges.PayrollView,
      },
    ],
  },
  {
    comomTitle: "Other",
    menuList: [
      {
        title: "Setting",
        submenuOpen: true,
        privileges: [
          Privileges.UserView,
          Privileges.BranchView,
          Privileges.CompanyView,
          Privileges.EmailView,
          Privileges.ThemeView,
        ],
        submenus: [
          { title: "User", privilege: Privileges.UserView },
          { title: "User Role", privilege: Privileges.UserView },
          { title: "Privilege", privilege: Privileges.UserView },
          { title: "Branch", privilege: Privileges.BranchView },
          { title: "Company", privilege: Privileges.CompanyView },
          { title: "Email", privilege: Privileges.EmailView },
          { title: "Theme Color", privilege: Privileges.ThemeView },
        ],
      },
    ],
  },
];

const Index = () => {
  const classes = useStyles();
  const navigate = useNavigate();
  const isMobileRes = useIsMobile();
  const isMobile = useIsMobile();
  const { showErrorMessage, showMessage } = useNotifier();
  const [searchQuery, setSearchQuery] = useState("");
  const [currentSearchQuery, setCurrentSearchQuery] = useState("");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(100);
  const debouncedSearchQuery = useDebounce(searchQuery, 300);
  const [expandedSections, setExpandedSections] = useState<
    Record<string, boolean>
  >({});

  const [selectedCoursing, setSelectedCoursing] = useState<IUserRole | null>(
    null
  );
  const [openDialog, setOpenDialog] = useState(false);
  const [openDeleteCategory, setOpenDeleteCategory] = useState(false);
  const [coursingToDelete, setCoursingToDelete] = useState<IUserRole | null>(
    null
  );

  const { data, isLoading, isError, refetch } = useGetUserRoleQuery({
    searchText: currentSearchQuery,
  });

  const { data: userData, isLoading: userIsLoading } = useGetUserInfoOnyQuery({
    searchText: "",
  });

  const { data: privileges, isLoading: privilegesLoading } =
    useGetAllUserPrivilegesQuery();

  const userList = useMemo(() => {
    return userData?.data as IUserAdmin[];
  }, [userData?.data]);

  const privilegesList = useMemo(() => {
    return privileges?.data as IPrivilege[];
  }, [privileges?.data]);

  const userMap = useMemo(() => {
    const map = new Map();
    userList?.forEach((user) => {
      map.set(
        user.id,
        `${toProperCase(user.user_role_name)} - ${user.username}`
      );
    });
    return map;
  }, [userList]);

  useEffect(() => {
    if (debouncedSearchQuery.length >= 4 || debouncedSearchQuery === "") {
      setCurrentSearchQuery(debouncedSearchQuery);
      refetch();
    }
  }, [debouncedSearchQuery]);

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleOpenDialog = (coursing: IUserRole) => {
    setSelectedCoursing(coursing);
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setSelectedCoursing(null);
    setOpenDialog(false);
  };

  const handleOpenDeletePopup = (coursing: IUserRole) => {
    setCoursingToDelete(coursing);
    setOpenDeleteCategory(true);
  };

  const handleCloseDeletePopup = () => {
    setCoursingToDelete(null);
    setOpenDeleteCategory(false);
  };

  const toggleSection = (sectionName: string) => {
    setExpandedSections((prev) => ({
      ...prev,
      [sectionName]: !prev[sectionName],
    }));
  };

  const [deleteCategory] = useDeleteUserRoleMutation();
  const handleDelete = async (id: string) => {
    try {
      const response = await deleteCategory(id).unwrap();
      if (response.status) {
        showMessage(response.message);
        setOpenDeleteCategory(false);
      } else {
        showErrorMessage(response.message);
      }
    } catch (error) {
      showErrorMessage("Failed to delete the user role");
    }
  };

  const coursings: IUserRole[] = Array.isArray(data?.data)
    ? (data?.data as IUserRole[])
    : [];

  const handleAddProductClick = () => {
    navigate("/user/role/create");
  };

  if (isLoading || userIsLoading)
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        height="10vh"
      >
        <CircularProgress />
      </Box>
    );

  return (
    <Card className={classes.card}>
      <Grid
        container
        justifyContent={"space-between"}
        spacing={5}
        sx={{ padding: 2 }}
        className={classes.titleGrid}
      >
        <Grid
          item
          lg={12}
          md={12}
          sm={12}
          xs={12}
          sx={{ display: "flex", justifyContent: "flex-end" }}
        >
          <Grid
            container
            spacing={1}
            justifyContent="flex-end"
            sx={{ display: "flex" }}
          >
            <Grid item xs={12} sm={6} md={4} lg={3}>
              <TextField
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <CiSearch className={classes.searchWidgetIcon} />
                    </InputAdornment>
                  ),
                  classes: {
                    root: classes.formControlList,
                  },
                }}
                fullWidth
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="User role  .."
                className={classes.searchTextField}
                size="small"
              />
            </Grid>
            <Grid item>
              <Link
                to={`/user/role/create`}
                className={classes.iconLinkPropNormal}
              >
                <Button
                  // variant="contained"
                  className={classes.addNewButton}
                  onClick={handleAddProductClick}
                >
                  Add New
                </Button>
              </Link>
            </Grid>
          </Grid>
        </Grid>
      </Grid>

      <Grid>
        <TableContainer>
          <Table className={classes.table}>
            <TableHead className={classes.tableHeaderRow}>
              <TableRow>
                <TableCell className={classes.tablheader}>
                  <strong>User Role Name</strong>
                </TableCell>

                <TableCell className={classes.tablheader} sx={{ width: "50%" }}>
                  <strong>privileges</strong>
                </TableCell>
                {!isMobileRes && (
                  <TableCell
                    className={classes.tablheader}
                    // sx={{ textAlign: "center" }}
                  >
                    <strong>Create By</strong>
                  </TableCell>
                )}
                <TableCell
                  className={classes.tablheader}
                  sx={{ textAlign: "right" }}
                >
                  <strong>Actions</strong>
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {isLoading || userIsLoading ? (
                <TableRow>
                  <TableCell colSpan={20} align="center">
                    <Box
                      display="flex"
                      justifyContent="center"
                      alignItems="center"
                      height="10vh"
                    >
                      <CircularProgress />
                    </Box>
                  </TableCell>
                </TableRow>
              ) : isError || coursings.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={20} align="center">
                    {isError ? "Error fetching data" : "No data available"}
                  </TableCell>
                </TableRow>
              ) : (
                coursings
                  .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                  .map((row: IUserRole, index) => (
                    <TableRow key={row.id} className={classes.tableRowHover}>
                      <TableCell
                        component="th"
                        scope="row"
                        className={classes.tablCell}
                      >
                        {toProperCase(row.roleName)}
                        {isMobileRes && (
                          <>
                            {row.created_by && (
                              <Typography className={classes.mobileListText}>
                                {`Created By : ${
                                  userMap.get(row.created_by) || ""
                                }`}
                              </Typography>
                            )}
                            {row.created_at && (
                              <Typography className={classes.mobileListText}>
                                {`Created At : ${dayjs(row.created_at).format(
                                  "MMM D, YYYY hh:mm A"
                                )}`}
                              </Typography>
                            )}
                          </>
                        )}
                      </TableCell>
                      <TableCell
                        className={classes.tablCell}
                        sx={{ width: "50%" }}
                      >
                        <Grid container spacing={1}>
                          {MainMenuList.map((section) => {
                            if (!privilegesList) return null;

                            // Get selected privilege titles
                            const selectedTitles: string[] = [];

                            section.menuList?.forEach((menu) => {
                              if (!row.privileges) return;

                              if (menu.privilege) {
                                const privId = privilegesList.find(
                                  (p) => p.name === menu.privilege
                                )?.id;

                                if (privId && row.privileges.includes(privId)) {
                                  selectedTitles.push(menu.title);
                                }
                              }

                              if (menu.privileges && menu.submenus) {
                                menu.submenus.forEach((sub) => {
                                  const subId = privilegesList.find(
                                    (p) => p.name === sub.privilege
                                  )?.id;

                                  if (subId && row.privileges.includes(subId)) {
                                    selectedTitles.push(sub.title);
                                  }
                                });
                              }
                            });

                            if (selectedTitles.length === 0) return null;

                            const isExpanded =
                              expandedSections[section.comomTitle] ?? false;

                            return (
                              <Grid item xs={12} key={section.comomTitle}>
                                <Typography fontWeight={700}>
                                  {section.comomTitle}
                                </Typography>

                                {!isExpanded && (
                                  <Typography variant="body2">
                                    {selectedTitles.join(", ")}{" "}
                                    {selectedTitles.length > 15 && (
                                      <Button
                                        size="small"
                                        onClick={() =>
                                          toggleSection(section.comomTitle)
                                        }
                                        sx={{
                                          fontSize: "14px",
                                          textTransform: "none",
                                        }}
                                      >
                                        Show All
                                      </Button>
                                    )}
                                  </Typography>
                                )}

                                {isExpanded && (
                                  <>
                                    <Box component="ul" sx={{ m: 0, pl: 3 }}>
                                      {selectedTitles.map((title) => (
                                        <li key={title}>
                                          <Typography variant="body2">
                                            {title}
                                          </Typography>
                                        </li>
                                      ))}
                                    </Box>

                                    <Button
                                      size="small"
                                      onClick={() =>
                                        toggleSection(section.comomTitle)
                                      }
                                    >
                                      Show Less
                                    </Button>
                                  </>
                                )}
                              </Grid>
                            );
                          })}
                        </Grid>
                      </TableCell>
                      {!isMobileRes && (
                        <TableCell
                          scope="row"
                          className={classes.tablCell}
                          // sx={{ textAlign: "center" }}
                        >
                          <Typography>
                            {userMap.get(row.created_by) || ""}
                          </Typography>
                          <Typography>
                            {dayjs(row.created_at).format(
                              "MMM D, YYYY hh:mm A"
                            )}
                          </Typography>
                        </TableCell>
                      )}
                      <TableCell className={classes.tablCell}>
                        {(String(row.id) != "2" ||
                          row.roleName != "Attendance Manager") && (
                          <Grid
                            container
                            spacing={1}
                            sx={{ display: "flex", justifyContent: "flex-end" }}
                          >
                            <Grid item>
                              <Button
                                // variant="contained"
                                className={classes.editButtonList}
                                onClick={() => handleOpenDialog(row)}
                              >
                                <EditSharpIcon
                                  className={classes.editListIcon}
                                />
                              </Button>
                            </Grid>
                            <Grid item>
                              <Button
                                // variant="contained"
                                className={classes.deleteButtonList}
                                onClick={() => handleOpenDeletePopup(row)}
                              >
                                <DeleteOutlineSharpIcon
                                  className={classes.deleteListIcon}
                                />
                              </Button>
                            </Grid>
                          </Grid>
                        )}
                      </TableCell>
                    </TableRow>
                  ))
              )}
            </TableBody>
          </Table>
          <Grid
            spacing={5}
            p={"18px"}
            py={4}
            justifyContent={"end"}
            container
            textAlign={"center"}
            alignContent={"center"}
            alignItems={"center"}
          >
            <Grid
              sx={{
                textAlign: "end",
                justifyContent: "flex-end",
                alignItems: "flex-end",
              }}
            >
              <Pagination
                count={coursings.length}
                page={page}
                rowsPerPage={rowsPerPage}
                onPageChange={handleChangePage}
              />
            </Grid>
          </Grid>
        </TableContainer>

        {selectedCoursing && (
          <NewUpdatePrivi
            openModel={openDialog}
            handleCloseDialog={handleCloseDialog}
            data={selectedCoursing}
          />
        )}
        {coursingToDelete && (
          <DeletePopup
            open={openDeleteCategory}
            handleCloseDelete={handleCloseDeletePopup}
            onConfirm={async () => {
              await handleDelete(coursingToDelete.id.toString());
            }}
            title="Delete User Role"
            content={`Are you sure you want to delete "${coursingToDelete.roleName}"?`}
          />
        )}
      </Grid>
    </Card>
  );
};

export default Index;
